import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowUpDown,
  XCircle,
  Search,
  ChevronRight,
  ChevronDown,
  Inbox as InboxIcon,
  SlidersHorizontal,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { UserSelector } from "@/components/UserSelector";
import { OpportunitySelector } from "@/components/OpportunitySelector";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import { getUserDisplayName, useOrgMembers } from "@/store/teamsStore";
import { useOpportunitiesStore } from "@/modules/opportunities/opportunitiesStore";
import { useOpportunitySettingsStore } from "@/store/opportunitySettingsStore";
import { getFilterableCustomFields } from "@/lib/customFieldFilters";
import { useFeatureFlag } from "@/store/featureFlagStore";
import Layout from "@/components/layout";
import { WorkflowRunViewWithOperations } from "@/modules/agents/components/WorkflowRunViewWithOperations";
import { StandardAgentThreadView } from "@/modules/standardAgent/components/StandardAgentThreadView";
import { CancelTaskConfirmationDialog } from "@/components/CancelTaskConfirmationDialog";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { useAgentWorkflowStore } from "@/store/agentsStore";
import { useAgentTasksStore } from "@/store/agentTasksStore";
import { getItemTitle, getItemDescription } from "@/pages/InboxItemRow";
import { InboxFilterBar } from "@/pages/InboxFilterBar";
import { InboxTable } from "@/pages/InboxTable";
import { InboxThreadGroup } from "@/pages/InboxThreadGroup";
import { groupInboxItemsByThread } from "@/pages/groupInboxItems";
import type { InboxTaskType } from "@/pages/inboxFilterTypes";
import {
  buildInboxTableRows,
  type InboxTableRow,
} from "@/pages/inboxTableRows";
import { INBOX_SORT_OPTIONS, type InboxSort } from "@/pages/inboxTableSort";
import {
  filterOnboardingTasks,
  OnboardingTasksInboxSection,
  useOnboardingInboxTasks,
} from "@/pages/OnboardingTasksInboxSection";
import { OnboardingTaskSheet } from "@/modules/onboarding/OnboardingTaskSheet";
import {
  AgentWorkflowRunTaskStatus,
  PendingInboxItem,
  InboxScopeEnum,
  Permission,
} from "@forgepoint/shared";

export default function Inbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [taskType, setTaskType] = useState<InboxTaskType | null>(null);
  const [sort, setSort] = useState<InboxSort>("newest");
  const [cancelItem, setCancelItem] = useState<PendingInboxItem | null>(null);
  const [onboardingTaskId, setOnboardingTaskId] = useState<string | null>(null);
  const [filterBarKey, setFilterBarKey] = useState(0);
  const { agentMap, fetchAgents } = useAgentWorkflowStore();
  const itemMap = useAgentTasksStore((state) => state.itemMap);
  const itemIds = useAgentTasksStore((state) => state.itemIds);
  const loadingState = useAgentTasksStore((state) => state.loadingState);
  const viewMode = useAgentTasksStore((state) => state.viewMode);
  const setViewMode = useAgentTasksStore((state) => state.setViewMode);
  const filterUserId = useAgentTasksStore((state) => state.filterUserId);
  const filterOpportunityId = useAgentTasksStore(
    (state) => state.filterOpportunityId,
  );
  const setFilterUserId = useAgentTasksStore((state) => state.setFilterUserId);
  const setFilterOpportunityId = useAgentTasksStore(
    (state) => state.setFilterOpportunityId,
  );
  const filterCustomFields = useAgentTasksStore(
    (state) => state.filterCustomFields,
  );
  const setFilterCustomFields = useAgentTasksStore(
    (state) => state.setFilterCustomFields,
  );
  const clearFilters = useAgentTasksStore((state) => state.clearFilters);
  const customFieldsSchema = useOpportunitySettingsStore(
    (state) => state.settings?.customFieldsSchema ?? null,
  );
  const fetchOpportunitySettings = useOpportunitySettingsStore(
    (state) => state.fetchSettings,
  );
  const fetchInboxItems = useAgentTasksStore((state) => state.fetchInboxItems);
  const removeInboxItem = useAgentTasksStore((state) => state.removeInboxItem);
  const restoreInboxItem = useAgentTasksStore(
    (state) => state.restoreInboxItem,
  );
  const cancelTask = useAgentTasksStore((state) => state.cancelTask);
  const currentUserId = useAuthStore((state) => state.user?.id);
  const isAdmin = useHasPermission(Permission.TASKS_MANAGE);
  const canViewAllTasks = useHasPermission(Permission.TASKS_VIEW_ALL);
  const canViewOnboarding = useHasPermission(Permission.ONBOARDING_VIEW);
  const onboardingTasks = useOnboardingInboxTasks();
  const userMap = useOrgMembers();
  const opportunityMap = useOpportunitiesStore((state) => state.opportunities);
  const tableView = useFeatureFlag("task-inbox-table-view");

  const selectedRunId = searchParams.get("runId");
  const selectedThreadId = searchParams.get("threadId");
  const standardAgentName = useStandardAgentDisplayName();
  const runViewOpen = selectedRunId !== null;
  const threadViewOpen = selectedThreadId !== null;

  const allItems = useMemo(
    () =>
      itemIds
        .map((itemId) => itemMap.get(itemId))
        .filter((item): item is PendingInboxItem => item !== undefined),
    [itemIds, itemMap],
  );

  const pendingItems = useMemo(
    () =>
      allItems.filter(
        (item) => item.status === AgentWorkflowRunTaskStatus.PENDING_REVIEW,
      ),
    [allItems],
  );

  const completedItems = useMemo(
    () =>
      allItems.filter(
        (item) => item.status !== AgentWorkflowRunTaskStatus.PENDING_REVIEW,
      ),
    [allItems],
  );

  const isLoading = loadingState.isLoadingGet;
  const error = loadingState.getError;

  const activeFilterCount =
    (viewMode === "ORGANIZATION" && filterUserId ? 1 : 0) +
    (filterOpportunityId ? 1 : 0);

  useEffect(() => {
    fetchInboxItems();
    fetchAgents();
  }, [fetchInboxItems, fetchAgents]);

  useEffect(() => {
    fetchOpportunitySettings();
  }, [fetchOpportunitySettings]);

  const filterableCustomFields = useMemo(
    () => getFilterableCustomFields(customFieldsSchema),
    [customFieldsSchema],
  );

  const filterItems = (items: PendingInboxItem[]) => {
    const searchLower = searchQuery.toLowerCase();
    if (!searchLower) {
      return items;
    }
    return items.filter((item) => {
      const title = getItemTitle(item);
      const description = getItemDescription(item, agentMap, standardAgentName);
      const oppName = item.opportunityName ?? "";
      const stageName = item.stageName ?? "";
      const taskDescription =
        item.sourceType === "agent_sdk_review" ? (item.description ?? "") : "";

      return (
        title.toLowerCase().includes(searchLower) ||
        description.toLowerCase().includes(searchLower) ||
        oppName.toLowerCase().includes(searchLower) ||
        stageName.toLowerCase().includes(searchLower) ||
        taskDescription.toLowerCase().includes(searchLower)
      );
    });
  };

  const filteredPending = useMemo(
    () => filterItems(pendingItems),
    [pendingItems, searchQuery, agentMap, standardAgentName],
  );

  const filteredCompleted = useMemo(
    () => filterItems(completedItems),
    [completedItems, searchQuery, agentMap, standardAgentName],
  );

  const filteredOnboardingTasks = useMemo(
    () => filterOnboardingTasks(onboardingTasks, searchQuery),
    [onboardingTasks, searchQuery],
  );

  const pendingGroups = useMemo(
    () => groupInboxItemsByThread(filteredPending),
    [filteredPending],
  );

  const completedGroups = useMemo(
    () => groupInboxItemsByThread(filteredCompleted),
    [filteredCompleted],
  );

  const tableRows = useMemo(
    () =>
      buildInboxTableRows({
        pendingItems: filteredPending,
        completedItems: filteredCompleted,
        onboardingTasks: filteredOnboardingTasks,
        showCompleted,
        sort,
        taskType,
        assigneeNameFor: (userId) => {
          const user = userMap.get(userId);
          return user ? getUserDisplayName(user) : "";
        },
      }),
    [
      filteredPending,
      filteredCompleted,
      filteredOnboardingTasks,
      showCompleted,
      sort,
      taskType,
      userMap,
    ],
  );

  const filterUserName = useMemo(() => {
    if (!filterUserId) {
      return null;
    }
    const user = userMap.get(filterUserId);
    return user ? getUserDisplayName(user) : null;
  }, [filterUserId, userMap]);

  const filterOpportunityName = useMemo(() => {
    if (!filterOpportunityId) {
      return null;
    }
    return (
      opportunityMap.get(filterOpportunityId)?.name ??
      allItems.find((item) => item.opportunityId === filterOpportunityId)
        ?.opportunityName ??
      null
    );
  }, [filterOpportunityId, opportunityMap, allItems]);

  const canCancelItem = useCallback(
    (item: PendingInboxItem) => {
      if (item.status !== AgentWorkflowRunTaskStatus.PENDING_REVIEW) {
        return false;
      }
      return isAdmin || item.assignees.some((a) => a.userId === currentUserId);
    },
    [isAdmin, currentUserId],
  );

  const handleItemClick = (item: PendingInboxItem) => {
    if (item.sourceType === "workflow_run_review") {
      setSearchParams({ runId: item.runId });
      return;
    }
    setSearchParams({ threadId: item.threadId });
  };

  const handleRowClick = (row: InboxTableRow) => {
    if (row.kind === "onboarding") {
      setOnboardingTaskId(row.task.id);
      return;
    }
    handleItemClick(row.item);
  };

  const handleCancelClick = (e: MouseEvent, item: PendingInboxItem) => {
    e.stopPropagation();
    setCancelItem(item);
  };

  const confirmCancel = async () => {
    if (!cancelItem) {
      return;
    }
    const removed = removeInboxItem(cancelItem.taskId);
    setCancelItem(null);
    if (!removed) {
      return;
    }
    const result = await cancelTask(removed.taskId);
    if (!result.ok) {
      restoreInboxItem(removed);
      toast.error(result.error ?? "Failed to cancel task");
    }
  };

  const clearTableFilters = () => {
    clearFilters();
    setTaskType(null);
    setShowCompleted(false);
  };

  /**
   * Clears the table filters and remounts the filter bar so its internal chip
   * state is reset alongside the filter values.
   * @returns Nothing
   */
  const clearTableFiltersAndChips = () => {
    clearTableFilters();
    setFilterBarKey((key) => key + 1);
  };

  const allCaughtUpState = (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="relative mb-4">
        <div className="rounded-full bg-muted/60 p-4">
          <InboxIcon className="h-8 w-8 text-muted-foreground/60" />
        </div>
        <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-background">
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        </span>
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1">
        All caught up
      </h3>
      <p className="text-sm text-muted-foreground">
        Nothing needs your review right now — nice work.
      </p>
    </div>
  );

  const narrowingFilterDescription = [
    searchQuery ? `matching \u201c${searchQuery}\u201d` : null,
    viewMode === "ORGANIZATION" && filterUserId
      ? `for ${filterUserName ?? "the selected assignee"}`
      : null,
    filterOpportunityId
      ? `on ${filterOpportunityName ?? "the selected opportunity"}`
      : null,
    Object.keys(filterCustomFields).length > 0
      ? "with the selected field values"
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" ");

  const emptyTaskNoun =
    taskType === "onboarding"
      ? "onboarding tasks"
      : taskType === "agent"
        ? "agent tasks"
        : "tasks";

  const tableEmptyState =
    narrowingFilterDescription || taskType ? (
      <div className="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center">
        <p className="text-sm text-muted-foreground">
          No {emptyTaskNoun} {narrowingFilterDescription}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-muted-foreground"
          onClick={() => {
            setSearchQuery("");
            clearTableFiltersAndChips();
          }}
        >
          Clear filters
        </Button>
      </div>
    ) : (
      allCaughtUpState
    );

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: "Inbox", current: true }]}>
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <XCircle className="h-10 w-10 text-destructive mb-3" />
          <h3 className="text-base font-semibold mb-1">
            Couldn&apos;t load your inbox
          </h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" size="sm" onClick={() => fetchInboxItems()}>
            Try again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: "Inbox", current: true }]}>
      <div className="flex flex-col h-full">
        {/* Sticky search header */}
        <div
          className={cn(
            "sticky top-0 z-10 bg-background",
            tableView ? "px-4 pt-3 pb-3" : "px-6 pt-6 pb-3",
          )}
        >
          <div
            className={cn(
              "flex items-center gap-1.5",
              tableView
                ? "gap-3"
                : "rounded-xl border bg-card py-1.5 pl-3 pr-1.5 shadow-sm transition-colors focus-within:border-ring/60 focus-within:ring-2 focus-within:ring-ring/15",
            )}
          >
            <div
              className={cn(
                "flex flex-1 items-center gap-1.5",
                tableView && "relative max-w-sm",
              )}
            >
              <Search
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground",
                  tableView && "absolute left-3 top-1/2 -translate-y-1/2",
                )}
              />
              <Input
                placeholder="Search tasks…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  "h-8 flex-1",
                  tableView
                    ? "rounded-md pl-9"
                    : "border-0 bg-transparent px-1 shadow-none focus-visible:ring-0",
                )}
              />
            </div>
            {tableView && (
              <InboxFilterBar
                key={filterBarKey}
                filterUserId={filterUserId}
                filterUserName={filterUserName}
                filterOpportunityId={filterOpportunityId}
                filterOpportunityName={filterOpportunityName}
                taskType={taskType}
                showCompleted={showCompleted}
                filterCustomFields={filterCustomFields}
                customFields={filterableCustomFields}
                onFilterUserIdChange={setFilterUserId}
                onFilterOpportunityIdChange={setFilterOpportunityId}
                onTaskTypeChange={setTaskType}
                onShowCompletedChange={setShowCompleted}
                onFilterCustomFieldsChange={setFilterCustomFields}
                onClearAll={clearTableFilters}
                canFilterByUser={viewMode === "ORGANIZATION"}
                canFilterByType={canViewOnboarding}
                canFilterCompleted={viewMode === "USER"}
              />
            )}
            <div
              className={cn(
                "flex items-center",
                tableView ? "ml-auto gap-2" : "gap-1.5",
              )}
            >
              {!isLoading &&
                pendingItems.length + onboardingTasks.length > 0 && (
                  <span className="mr-1 hidden shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground tabular-nums sm:inline-flex">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    {pendingItems.length + onboardingTasks.length} pending
                  </span>
                )}
              {tableView && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 bg-background font-medium"
                    >
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                      {
                        INBOX_SORT_OPTIONS.find(
                          (option) => option.value === sort,
                        )?.label
                      }
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuRadioGroup
                      value={sort}
                      onValueChange={(value) =>
                        setSort(
                          INBOX_SORT_OPTIONS.find(
                            (option) => option.value === value,
                          )?.value ?? sort,
                        )
                      }
                    >
                      {INBOX_SORT_OPTIONS.map((option) => (
                        <DropdownMenuRadioItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {canViewAllTasks && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLoading}
                      className="h-8 shrink-0 bg-background font-medium"
                    >
                      <InboxIcon className="h-4 w-4 text-muted-foreground" />
                      {viewMode === "ORGANIZATION"
                        ? "Company inbox"
                        : "My inbox"}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuRadioGroup
                      value={viewMode}
                      onValueChange={(value) =>
                        setViewMode(InboxScopeEnum.catch("USER").parse(value))
                      }
                    >
                      <DropdownMenuRadioItem value="USER">
                        My inbox
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="ORGANIZATION">
                        Company inbox
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {!tableView && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 bg-background font-medium"
                    >
                      <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                      Filter
                      {activeFilterCount > 0 && (
                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground tabular-nums">
                          {activeFilterCount}
                        </span>
                      )}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 space-y-3">
                    {viewMode === "ORGANIZATION" && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">
                          Assignee
                        </p>
                        <UserSelector
                          currentlySelectedId={filterUserId}
                          onSelectId={setFilterUserId}
                          placeholder="Anyone"
                          className="w-full"
                        />
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">
                        Opportunity
                      </p>
                      <OpportunitySelector
                        currentlySelectedId={filterOpportunityId}
                        onSelectId={setFilterOpportunityId}
                        placeholder="Any opportunity"
                        className="w-full"
                      />
                    </div>
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-full text-muted-foreground"
                        onClick={clearFilters}
                      >
                        Clear filters
                      </Button>
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className={cn(
            "flex-1 min-h-0 overflow-y-auto",
            tableView && "flex flex-col",
          )}
        >
          {isLoading && allItems.length === 0 ? (
            <div className="px-6 pt-4 pb-6">
              <LoadingSkeleton />
            </div>
          ) : tableView ? (
            <InboxTable
              rows={tableRows}
              isLoading={isLoading && tableRows.length === 0}
              currentUserId={currentUserId}
              showStatus={showCompleted}
              onClickRow={handleRowClick}
              canCancel={canCancelItem}
              onCancel={handleCancelClick}
              emptyState={tableEmptyState}
            />
          ) : !isLoading &&
            pendingItems.length === 0 &&
            onboardingTasks.length === 0 &&
            !showCompleted ? (
            allCaughtUpState
          ) : filteredPending.length === 0 &&
            filteredOnboardingTasks.length === 0 &&
            searchQuery ? (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <p className="text-sm text-muted-foreground">
                No pending tasks for &ldquo;{searchQuery}&rdquo;
              </p>
            </div>
          ) : (
            <>
              {pendingGroups.length > 0 && (
                <div className="flex flex-col gap-2 px-6 pt-4">
                  {pendingGroups.map((group) => (
                    <InboxThreadGroup
                      key={group.key}
                      group={group}
                      agentMap={agentMap}
                      standardAgentName={standardAgentName}
                      onItemClick={handleItemClick}
                      onItemCancel={handleCancelClick}
                      isAdmin={isAdmin}
                      currentUserId={currentUserId}
                    />
                  ))}
                </div>
              )}
              <OnboardingTasksInboxSection
                tasks={filteredOnboardingTasks}
                onOpenTask={setOnboardingTaskId}
              />
            </>
          )}

          {/* Completed tasks section */}
          {!tableView && completedItems.length > 0 && (
            <div className="px-6 mt-2 pb-6">
              <div className="border-t pt-3">
                <button
                  className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowCompleted(!showCompleted)}
                >
                  {showCompleted ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  Completed
                  <span className="rounded-full bg-muted px-1.5 py-px text-[10px] tabular-nums">
                    {completedItems.length}
                  </span>
                </button>

                {showCompleted && (
                  <div className="flex flex-col gap-2 mt-2">
                    {filteredCompleted.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No completed tasks for &ldquo;{searchQuery}&rdquo;
                      </p>
                    ) : (
                      completedGroups.map((group) => (
                        <InboxThreadGroup
                          key={group.key}
                          group={group}
                          agentMap={agentMap}
                          standardAgentName={standardAgentName}
                          onItemClick={handleItemClick}
                          completed
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <WorkflowRunViewWithOperations
        runId={selectedRunId}
        isOpen={runViewOpen}
        onClose={() => {
          setSearchParams({});
          fetchInboxItems();
        }}
      />

      <StandardAgentThreadView
        threadId={selectedThreadId}
        isOpen={threadViewOpen}
        onClose={() => {
          setSearchParams({});
          fetchInboxItems();
        }}
        enableTaskDeepLink
      />

      <OnboardingTaskSheet
        taskId={onboardingTaskId}
        onClose={() => setOnboardingTaskId(null)}
      />

      <CancelTaskConfirmationDialog
        open={cancelItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCancelItem(null);
          }
        }}
        onConfirm={confirmCancel}
        agentName={standardAgentName}
        taskSubject={cancelItem ? getItemTitle(cancelItem) : undefined}
      />
    </Layout>
  );
}

/**
 * Skeleton loading state that mimics the inbox row layout.
 */
function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="h-2 w-2 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}
